import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateUsername, validatePassword, formatAuthError } from '../../utils/auth';
import { 
  Button, 
  Input, 
  Card, 
  CardBody 
} from '@heroui/react';
import { 
  EyeIcon, 
  EyeSlashIcon,
  UserIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const LoginForm = () => {
  const { login, error, clearError, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate username
    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.isValid) {
      errors.username = usernameValidation.message;
    }
    
    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.message;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any existing errors before attempting login
    clearError();
    
    if (!validateForm()) {
      return;
    }

    try {
      await login(formData);
      
      // Get the intended destination or default to dashboard
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
      
    } catch (error) {
      // Error is handled by AuthContext and will be displayed in UI
      console.error('Login error:', error);
    }
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Username Field */}
      <div>
        <Input
          label="Username"
          placeholder="Enter your username"
          value={formData.username}
          onChange={(e) => handleInputChange('username', e.target.value)}
          isRequired
          isInvalid={!!validationErrors.username}
          errorMessage={validationErrors.username}
          startContent={
            <UserIcon className="w-4 h-4 text-gray-400" />
          }
          className="w-full"
          autoComplete="username"
        />
      </div>

      {/* Password Field */}
      <div>
        <Input
          label="Password"
          placeholder="Enter your password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          isRequired
          isInvalid={!!validationErrors.password}
          errorMessage={validationErrors.password}
          startContent={
            <LockClosedIcon className="w-4 h-4 text-gray-400" />
          }
          endContent={
            <button
              className="focus:outline-none"
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeSlashIcon className="w-4 h-4 text-gray-400" />
              ) : (
                <EyeIcon className="w-4 h-4 text-gray-400" />
              )}
            </button>
          }
          className="w-full"
          autoComplete="current-password"
        />
      </div>

      {/* Error Message */}
      {error && (
        <Card className="bg-red-50 border border-red-200">
          <CardBody className="py-2">
            <p className="text-sm text-red-700">{formatAuthError(error)}</p>
          </CardBody>
        </Card>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        color="primary"
        size="lg"
        className="w-full"
        isLoading={isLoading}
        isDisabled={!formData.username.trim() || !formData.password.trim()}
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </Button>

    </form>
  );
};

export default LoginForm;
